import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Flag,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { ExamContentGuard } from '../components/exam/ExamContentGuard';
import { DevToolsBlockOverlay } from '../components/exam/DevToolsBlockOverlay';
import { useDevToolsGuard } from '../hooks/useDevToolsGuard';
import { examService, type ResumeAttemptResponse } from '../services/exam.service';

interface ReadingQuestion {
  id: string;
  textTitle: string;
  textContent: string;
  question: string;
  options: string[];
}

function mapResumeToQuestions(data: ResumeAttemptResponse): ReadingQuestion[] {
  const exam = data.exam;
  if (!exam?.questions?.length) return [];
  return exam.questions.map((q) => ({
    id: q._id,
    textTitle: q.passageTitle || exam.title || 'Tekst',
    textContent: q.text || '',
    question: q.prompt || q.text || '',
    options: (q.options || []).map((o) => o.text),
  }));
}

function getInitialAnswers(data: ResumeAttemptResponse): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const exam = data.exam;
  if (!exam?.questions?.length) return out;
  const saved = data.savedAnswers || [];
  exam.questions.forEach((q, idx) => {
    const savedEntry = saved.find(
      (a) => String(a.questionId) === String(q._id),
    );
    if (savedEntry != null && typeof savedEntry.answer === 'string') {
      const optionIdx = (q.options || []).findIndex(
        (o) => o.text === savedEntry.answer,
      );
      out[q._id] = optionIdx >= 0 ? optionIdx : null;
    } else {
      out[q._id] = null;
    }
  });
  return out;
}

const SAVE_DEBOUNCE_MS = 500;

export const ReadingExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const { devToolsAllowed, devToolsOpen } = useDevToolsGuard(searchParams);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resumeData, setResumeData] = useState<ResumeAttemptResponse | null>(null);
  const [questions, setQuestions] = useState<ReadingQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number | null>>({});
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auth is enforced by ProtectedRoute

  useEffect(() => {
    if (!attemptId) {
      navigate('/dashboard');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    examService
      .resumeAttempt(attemptId)
      .then((data) => {
        if (cancelled) return;
        if (data.exam?.type !== 'reading') {
          setLoadError('This is not a reading exam.');
          setLoading(false);
          return;
        }
        const mapped = mapResumeToQuestions(data);
        if (mapped.length === 0) {
          setLoadError('No questions in this exam.');
          setLoading(false);
          return;
        }
        setResumeData(data);
        setQuestions(mapped);
        setAnswers(getInitialAnswers(data));
        const remaining = Math.max(
          0,
          Math.floor(
            (new Date(data.expiresAt).getTime() - Date.now()) / 1000,
          ),
        );
        setRemainingSeconds(remaining);
        setLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message || 'Could not load exam. You may need to start again.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [attemptId, navigate]);

  const current = questions[currentIndex];

  const handleSubmit = useCallback(async () => {
    if (!attemptId || submitting) return;
    setSubmitting(true);
    try {
      await examService.submitExam(attemptId);
      navigate(`/results?attemptId=${attemptId}`);
    } catch (err: any) {
      setSubmitting(false);
      setLoadError(err?.response?.data?.message || 'Failed to submit. Please try again.');
    }
  }, [attemptId, navigate, submitting]);

  useEffect(() => {
    if (!attemptId || !resumeData || questions.length === 0) return;
    const expiresAt = new Date(resumeData.expiresAt).getTime();
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        examService.submitExam(attemptId).then(() => {
          navigate(`/results?attemptId=${attemptId}`);
        }).catch(() => {});
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [attemptId, resumeData, questions.length, navigate]);

  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const saveAnswerToBackend = useCallback(
    (questionId: string, optionText: string) => {
      if (!attemptId) return;
      examService.saveAnswer(attemptId, questionId, optionText).catch(() => {
        // Optionally show a subtle toast; for now ignore to avoid blocking UI
      });
    },
    [attemptId],
  );

  const handleSelectOption = useCallback(
    (idx: number) => {
      if (!current) return;
      const optionText = current.options[idx];
      setAnswers((prev) => ({ ...prev, [current.id]: idx }));

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswerToBackend(current.id, optionText);
        saveTimeoutRef.current = null;
      }, SAVE_DEBOUNCE_MS);
    },
    [current, saveAnswerToBackend],
  );

  const handleToggleFlag = () => {
    if (!current) return;
    setFlagged((prev) => ({ ...prev, [current.id]: !prev[current.id] }));
  };

  const navigateQuestion = (offset: number) => {
    setCurrentIndex((prev) => {
      const next = prev + offset;
      if (next < 0 || next >= questions.length) return prev;
      return next;
    });
  };

  const answeredCount = Object.values(answers).filter(
    (v) => v !== null && v !== undefined,
  ).length;
  const flaggedCount = Object.values(flagged).filter(Boolean).length;
  const isLowTime = remainingSeconds < 300;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">Loading exam…</p>
        </div>
      </div>
    );
  }

  if (loadError || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">{loadError || 'No questions available.'}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <DevToolsBlockOverlay show={devToolsOpen} />
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('exam.reading.title')}</h1>
                <p className="text-xs text-gray-500">
                  {resumeData?.exam?.level || ''} · {questions.length} {t('exam.common.questions')}
                </p>
              </div>
            </div>
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg ${
                isLowTime ? 'bg-error/10 text-error animate-pulse' : 'bg-gray-900 text-white'
              }`}
            >
              <Clock className="w-5 h-5" />
              <span className="font-bold">{formatTime(remainingSeconds)}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 bg-gray-200 h-2 rounded-full">
              <div
                className="h-2 rounded-full bg-secondary transition-all duration-300"
                style={{ width: `${(answeredCount / questions.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {answeredCount}/{questions.length} {t('exam.common.answered')}
            </span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <ExamContentGuard disabled={devToolsAllowed}>
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-lg font-bold text-primary mb-3">{current.textTitle}</h2>
                <div className="bg-gray-50 rounded-xl p-4 text-gray-700 leading-relaxed whitespace-pre-line">
                  {current.textContent}
                </div>
              </div>
            </ExamContentGuard>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-primary/10 text-primary font-bold rounded-full text-sm">
                    {t('exam.common.question')} {currentIndex + 1}
                  </span>
                  {flagged[current.id] && (
                    <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full flex items-center gap-1">
                      <Flag className="w-3 h-3" /> {t('exam.common.flagged')}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleToggleFlag}
                  className={`p-2 rounded-lg transition-colors ${
                    flagged[current.id]
                      ? 'bg-warning/10 text-warning'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <Flag className="w-5 h-5" />
                </button>
              </div>

              <ExamContentGuard disabled={devToolsAllowed}>
                <p className="text-gray-900 font-medium mb-4">{current.question}</p>

                <div className="space-y-3">
                  {current.options.map((opt, idx) => {
                  const selected = answers[current.id] === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSelectOption(idx)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        selected
                          ? 'border-secondary bg-secondary/10 shadow-md'
                          : 'border-gray-200 hover:border-secondary/50 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 font-bold text-sm ${
                          selected ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className={selected ? 'font-medium' : ''}>{opt}</span>
                    </button>
                  );
                })}
                </div>
              </ExamContentGuard>

              <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
                <Button
                  variant="tertiary"
                  onClick={() => navigateQuestion(-1)}
                  disabled={currentIndex === 0}
                  icon={<ChevronLeft className="w-4 h-4" />}
                >
                  {t('exam.common.previous')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigateQuestion(1)}
                  disabled={currentIndex === questions.length - 1}
                  className="flex-row-reverse"
                  icon={<ChevronRight className="w-4 h-4" />}
                >
                  {t('exam.common.next')}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 sticky top-32">
              <h3 className="font-bold text-gray-900 mb-3">{t('exam.common.questionNavigator')}</h3>
              <div className="grid grid-cols-6 lg:grid-cols-4 gap-2 mb-4">
                {questions.map((q, idx) => {
                  const isAnswered = answers[q.id] !== null && answers[q.id] !== undefined;
                  const isFlagged = flagged[q.id];
                  const isCurrent = idx === currentIndex;
                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-full aspect-square rounded-lg text-sm font-medium flex items-center justify-center transition-all ${
                        isCurrent
                          ? 'bg-primary text-white ring-2 ring-primary ring-offset-2'
                          : isAnswered
                          ? 'bg-success/20 text-success'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } ${isFlagged ? 'ring-2 ring-warning' : ''}`}
                    >
                      {isFlagged ? <Flag className="w-3 h-3" /> : idx + 1}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-2 text-xs text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-success/20" />
                  <span>{t('exam.common.answered')} ({answeredCount})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-100" />
                  <span>{t('exam.common.notAnswered')} ({questions.length - answeredCount})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-100 ring-2 ring-warning" />
                  <span>{t('exam.common.flagged')} ({flaggedCount})</span>
                </div>
              </div>
              <Button
                variant="primary"
                className="w-full bg-success hover:bg-success/90"
                onClick={() => setShowSubmitModal(true)}
              >
                {t('exam.common.submitExam')}
              </Button>
            </div>
          </div>
        </div>
      </main>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('exam.common.confirmSubmit')}</h3>
                <p className="text-sm text-gray-500">{t('exam.common.cannotUndo')}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('exam.common.answered')}:</span>
                <span className="font-bold text-success">{answeredCount}/{questions.length}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('exam.common.notAnswered')}:</span>
                <span className="font-bold text-error">{questions.length - answeredCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('exam.common.flagged')}:</span>
                <span className="font-bold text-warning">{flaggedCount}</span>
              </div>
            </div>
            {questions.length - answeredCount > 0 && (
              <p className="text-sm text-error mb-4">
                ⚠️ {t('exam.common.unansweredWarning', { count: questions.length - answeredCount })}
              </p>
            )}
            <div className="flex gap-3">
              <Button
                variant="tertiary"
                className="flex-1"
                onClick={() => setShowSubmitModal(false)}
                disabled={submitting}
              >
                {t('exam.common.backToExam')}
              </Button>
              <Button
                variant="primary"
                className="flex-1 bg-success hover:bg-success/90"
                loading={submitting}
                onClick={handleSubmit}
                icon={<CheckCircle className="w-4 h-4" />}
              >
                {t('exam.common.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingExamPage;
